import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader, Moon, Sun } from 'lucide-react';
import { MenuItem, ordersApi } from '../services/ordersApi';
import { DineInThemeShell } from '../components/DineInThemeShell';
import { MenuFavoriteButton } from '../components/MenuFavoriteButton';
import { useDineInMenuTheme } from '../hooks/useDineInMenuTheme';

export default function DineInProductScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme, isLight, tokens, toggleTheme, fadeMs } = useDineInMenuTheme();
  const [dish, setDish] = useState<MenuItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    ordersApi.getMenuItem(id, 'dine-in').then((item) => {
      setDish(item);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <DineInThemeShell theme={theme} fadeMs={fadeMs}>
        <div className="min-h-screen flex items-center justify-center">
          <Loader className="animate-spin" size={28} style={{ color: tokens.muted }} />
        </div>
      </DineInThemeShell>
    );
  }

  if (!dish) {
    return (
      <DineInThemeShell theme={theme} fadeMs={fadeMs}>
        <div
          className="min-h-screen flex flex-col items-center justify-center gap-4 px-6"
          style={{ color: tokens.text }}
        >
          <p style={{ color: tokens.muted, fontFamily: 'Manrope, sans-serif' }}>
            Блюдо не найдено
          </p>
          <button
            type="button"
            onClick={() => navigate('/menu')}
            className="text-[12px] tracking-[0.16em] uppercase font-semibold"
            style={{ color: tokens.text, fontFamily: 'Manrope, sans-serif' }}
          >
            К меню
          </button>
        </div>
      </DineInThemeShell>
    );
  }

  return (
    <DineInThemeShell theme={theme} fadeMs={fadeMs}>
      <div className="min-h-screen flex flex-col" style={{ color: tokens.text }}>
        <div className="max-w-md mx-auto w-full flex flex-col flex-1 px-4 pt-5 pb-12">
          <div className="flex items-center justify-between mb-10">
            <button
              type="button"
              onClick={() => navigate('/menu')}
              className="flex items-center gap-0.5 text-[12px] tracking-[0.14em] uppercase"
              style={{ color: tokens.muted, fontFamily: 'Manrope, sans-serif' }}
            >
              <ArrowLeft size={16} /> Меню
            </button>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={isLight ? 'Тёмная тема' : 'Светлая тема'}
              className="w-9 h-9 flex items-center justify-center active:opacity-70"
              style={{
                border: `1px solid ${tokens.border}`,
                color: tokens.text,
              }}
            >
              {isLight ? <Moon size={16} /> : <Sun size={16} />}
            </button>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] as const }}
          >
            {dish.isUnavailable && (
              <p
                className="text-[11px] font-semibold tracking-[0.14em] uppercase mb-4"
                style={{ color: tokens.warn, fontFamily: 'Manrope, sans-serif' }}
              >
                Нет в наличии
              </p>
            )}

            <div className="flex items-start justify-between gap-4 mb-6">
              <h1
                className="text-[1.75rem] sm:text-3xl font-bold leading-tight flex-1"
                style={{
                  fontFamily: 'Unbounded, sans-serif',
                  letterSpacing: '-0.03em',
                }}
              >
                {dish.name}
              </h1>
              <div className="shrink-0 flex items-center gap-2">
                <MenuFavoriteButton
                  itemId={dish.id}
                  size={20}
                  className="w-10 h-10"
                  activeColor={tokens.text}
                  inactiveColor={tokens.navInactive}
                />
                {dish.price > 0 && (
                  <span
                    className="text-[1.75rem] font-bold tabular-nums"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {dish.price} ₽
                  </span>
                )}
              </div>
            </div>

            {dish.nutrition && (
              <div
                className="grid grid-cols-4 gap-2 mb-8 py-4"
                style={{
                  borderTop: `1px solid ${tokens.border}`,
                  borderBottom: `1px solid ${tokens.border}`,
                }}
              >
                {[
                  { label: 'Ккал', value: dish.nutrition.energy },
                  { label: 'Белки', value: `${dish.nutrition.proteins}г` },
                  { label: 'Жиры', value: `${dish.nutrition.fats}г` },
                  { label: 'Углев.', value: `${dish.nutrition.carbs}г` },
                ].map((item) => (
                  <div key={item.label} className="text-center">
                    <div
                      className="text-base font-bold"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      {item.value}
                    </div>
                    <div
                      className="text-[10px] tracking-[0.12em] uppercase mt-1"
                      style={{ color: tokens.navInactive, fontFamily: 'Manrope, sans-serif' }}
                    >
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {dish.description && (
              <div>
                <h3
                  className="text-[11px] font-semibold uppercase tracking-[0.22em] mb-3"
                  style={{ color: tokens.navInactive, fontFamily: 'Manrope, sans-serif' }}
                >
                  О блюде
                </h3>
                <p
                  className="text-[15px] leading-relaxed"
                  style={{ color: tokens.mutedSoft, fontFamily: 'Manrope, sans-serif' }}
                >
                  {dish.description}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </DineInThemeShell>
  );
}
